import { useRef } from 'react';
import { LayoutDashboard, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PageShell } from '@/shared/components';
import { useAccDashboard } from '../hooks/useAccDashboard';
import { KpiRow, HealthBadge } from '../components/KpiRow';
import { ActivityFeed } from '../components/ActivityFeed';
import { ReviewQueue } from '../components/ReviewQueue';

export function AccDashboardPage() {
  const reviewQueueRef = useRef<HTMLDivElement>(null);
  const cashQueueRef = useRef<HTMLDivElement>(null);

  const { data, isLoading, isFetching, refetch } = useAccDashboard();

  const degraded = data?.degraded ?? [];

  function scrollToReviewQueue() {
    reviewQueueRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function scrollToCashQueue() {
    // Cash-to-apply is shown in the review queue section for now
    cashQueueRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  return (
    <PageShell
      Icon={LayoutDashboard}
      subtitle="Bookkeeper health at a glance — what needs you, what the engine processed."
      actions={
        <div className="flex items-center gap-3">
          {data?.health && (
            <HealthBadge state={data.health.state} reason={data.health.reason} />
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => void refetch()}
            disabled={isFetching}
            aria-label="Refresh dashboard"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        {/* KPI strip */}
        <KpiRow
          kpis={
            data?.kpis ?? {
              needsReview: null,
              cashToApply: null,
              unreconciled: null,
              straightThroughRate: null,
              avgOpenDays: null,
            }
          }
          degraded={degraded}
          loading={isLoading}
          onScrollToReviewQueue={scrollToReviewQueue}
          onScrollToCashQueue={scrollToCashQueue}
        />

        {/* Review queue */}
        <section ref={reviewQueueRef} aria-labelledby="review-queue-heading">
          <h2
            id="review-queue-heading"
            className="text-sm font-semibold text-foreground mb-3"
          >
            Review queue
          </h2>
          <ReviewQueue
            items={data?.reviewQueue ?? null}
            degraded={degraded.includes('reviewQueue')}
            loading={isLoading}
          />
        </section>

        {/* Activity feed */}
        <section ref={cashQueueRef} aria-labelledby="activity-feed-heading">
          <h2
            id="activity-feed-heading"
            className="text-sm font-semibold text-foreground mb-3"
          >
            Activity feed
          </h2>
          <ActivityFeed
            items={data?.activityFeed ?? null}
            degraded={degraded.includes('activityFeed')}
            loading={isLoading}
          />
        </section>
      </div>
    </PageShell>
  );
}
